import React, { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { MoreVertical } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { LoaderIcon } from '@dashboard/components/ui/loader-icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';

import { Badge } from '@dashboard/components/ui/badge';
import Dash from '@dashboard/components/Dash';
import { DataGridColumnHeader } from '@dashboard/components/DataGrid/DataGridColumnHeader';
import { getDisplayStatusInfo } from '@dashboard/components/EmailLogColumns';
import { formatDateTime } from '@dashboard/lib/format-date';
import DeleteStaffDialog from './DeleteStaffDialog';
import TicketTypeRestriction, { type TicketTypeOption } from './TicketTypeRestriction';
import type { StaffInvite } from '../hooks/useStaffInvites';

export type RowPending = {
  resend?: boolean;
  reinvite?: boolean;
  revoke?: boolean;
  remove?: boolean;
};

export interface StaffColumnsContext {
  canManage: boolean;
  rowPending: (invite: StaffInvite) => RowPending;
  onResend: (userId: string) => void;
  onReinvite: (email: string) => void;
  onRevoke: (userId: string) => void;
  onRemove: (userId: string) => void;
  /** Ticket-type options for the Permissions cell. Empty = event has no ticket types. */
  ticketTypeOptions: TicketTypeOption[];
  /** Update a staffer's ticket-type restrictions inline from the cell. */
  onUpdatePermissions: (userId: string, allowedTicketTypes: string[]) => void;
}

export const STATUS_LABEL: Record<StaffInvite['status'], string> = {
  pending: 'Invited',
  active: 'Accepted',
  revoked: 'Access removed',
  deleted: 'Deleted',
};

const STATUS_VARIANT: Record<
  StaffInvite['status'],
  'info' | 'success' | 'outline' | 'destructive'
> = {
  pending: 'info',
  active: 'success',
  revoked: 'outline',
  deleted: 'destructive',
};

function StaffActionsCell({ invite, ctx }: { invite: StaffInvite; ctx: StaffColumnsContext }) {
  const [openCount, setOpenCount] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const pending = ctx.rowPending(invite);
  const anyPending = !!(pending.resend || pending.reinvite || pending.revoke || pending.remove);
  const canResend = invite.status === 'pending' || invite.status === 'active';
  const canStopAccess = invite.status === 'pending' || invite.status === 'active';
  const canReinvite = invite.status === 'revoked' || invite.status === 'deleted';
  const isRemoved = invite.status === 'deleted';

  if (!ctx.canManage) return null;

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="size-8 p-0"
            disabled={anyPending}
            aria-label={`Actions for ${invite.email}`}
          >
            {anyPending ? <LoaderIcon className="size-3.5" /> : <MoreVertical className="size-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canResend && (
            <DropdownMenuItem onSelect={() => ctx.onResend(invite.userId)}>
              {pending.resend ? 'Resending…' : 'Resend invite'}
            </DropdownMenuItem>
          )}
          {canReinvite && (
            <DropdownMenuItem onSelect={() => ctx.onReinvite(invite.email)}>
              {pending.reinvite ? 'Re-inviting…' : 'Re-invite'}
            </DropdownMenuItem>
          )}
          {canStopAccess && (
            <DropdownMenuItem onSelect={() => ctx.onRevoke(invite.userId)}>
              {pending.revoke ? 'Removing access…' : 'Remove access'}
            </DropdownMenuItem>
          )}
          {!isRemoved && (canResend || canReinvite || canStopAccess) && <DropdownMenuSeparator />}
          {!isRemoved && (
            <DropdownMenuItem
              onSelect={e => {
                e.preventDefault();
                setOpenCount(c => c + 1);
                setDeleteOpen(true);
              }}
              className="text-destructive focus:text-destructive"
            >
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteStaffDialog
        key={openCount}
        open={deleteOpen}
        email={invite.email}
        pending={pending.remove}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteOpen(false);
          ctx.onRemove(invite.userId);
        }}
      />
    </>
  );
}

export function getStaffColumns(ctx: StaffColumnsContext): ColumnDef<StaffInvite, unknown>[] {
  return [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
      enableSorting: false,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) =>
        row.original.name ? (
          <span className="text-muted-foreground">{row.original.name}</span>
        ) : (
          <Dash />
        ),
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Status" />,
      cell: ({ row }) => (
        <Badge variant={STATUS_VARIANT[row.original.status]}>
          {STATUS_LABEL[row.original.status]}
        </Badge>
      ),
      // Server-side sort. Logical order: active → pending → revoked → removed.
      enableSorting: true,
    },
    {
      id: 'permissions',
      header: () => <span className="inline-flex items-center gap-1">Ticket scanning</span>,
      cell: ({ row }) => {
        const inert = row.original.status === 'revoked' || row.original.status === 'deleted';
        if (inert) return <Dash />;
        return (
          <TicketTypeRestriction
            options={ctx.ticketTypeOptions}
            value={row.original.allowedTicketTypes ?? []}
            onChange={next => ctx.onUpdatePermissions(row.original.userId, next)}
            inline
            disabled={!ctx.canManage}
          />
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'invitedAt',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Invited" />,
      cell: ({ row }) =>
        row.original.invitedAt ? (
          <span className="text-muted-foreground text-sm">
            {formatDateTime(row.original.invitedAt)}
          </span>
        ) : (
          <Dash />
        ),
      // Default sort. Most recently invited first — that's the row a
      // merchant who just hit "Send invites" expects to see at the top.
      enableSorting: true,
    },
    {
      accessorKey: 'lastSeenAt',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Last seen" />,
      cell: ({ row }) =>
        row.original.lastSeenAt ? (
          <span className="text-muted-foreground text-sm">
            {formatDateTime(row.original.lastSeenAt)}
          </span>
        ) : (
          <Dash />
        ),
      // Server-side sort. Defaults to desc (most-recent-first). Nulls go
      // to the bottom on desc and to the top on asc — acceptable since
      // ascending lastSeenAt rarely matters in practice.
      enableSorting: true,
    },
    {
      id: 'sendStatus',
      header: 'Send status',
      cell: ({ row }) => {
        const log = row.original.inviteEmail;
        if (!log?.status) return <Dash />;
        const { text, appearance } = getDisplayStatusInfo(log);
        const variant = appearance === 'error' ? 'destructive' : appearance;
        return <Badge variant={variant}>{text}</Badge>;
      },
      enableSorting: false,
    },
    {
      id: 'delivery',
      header: 'Delivery',
      cell: ({ row }) => {
        const log = row.original.inviteEmail;
        if (!log) return <Dash />;
        if (log.delivered === true && log.deliveredAt) return formatDateTime(log.deliveredAt);
        if (log.delivered === true) return <Badge variant="success">Delivered</Badge>;
        if (log.delivered === false) return <Badge variant="info">Pending</Badge>;
        return <Dash />;
      },
      enableSorting: false,
    },
    {
      id: 'opened',
      header: 'Opened',
      cell: ({ row }) => {
        const log = row.original.inviteEmail;
        if (!log) return <Dash />;
        if (log.opened === true && log.openedAt) return formatDateTime(log.openedAt);
        if (log.opened === true) return <Badge variant="success">Opened</Badge>;
        if (log.opened === false) return <Badge variant="info">Unopened</Badge>;
        return <Dash />;
      },
      enableSorting: false,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => <StaffActionsCell invite={row.original} ctx={ctx} />,
      enableSorting: false,
      meta: { className: 'text-right' },
    },
  ];
}
